import React, {
  createContext,
  useState,
  useContext,
  useRef,
  useCallback,
} from 'react';
import LoadingScreen from '../components/LoadingScreen';

const LoadingContext = createContext();

export function useLoading() {
  const context = useContext(LoadingContext);
  if (!context) {
    throw new Error('useLoading must be used within a LoadingProvider');
  }
  return context;
}

export function LoadingProvider({ children }) {
  const [loadingState, setLoadingState] = useState({
    isLoading: false,
    message: null,
  });
  const timeoutRef = useRef(null);
  const isLoadingRef = useRef(false);
  const messageRef = useRef(null);

  const showLoading = useCallback((message = null) => {
    // Clear any pending hide timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // If already loading with the same message, don't update to prevent re-render
    if (isLoadingRef.current && messageRef.current === message) {
      return;
    }

    // Update states together to minimize re-renders
    messageRef.current = message;
    isLoadingRef.current = true;

    // Single state update to prevent multiple re-renders
    setLoadingState({ isLoading: true, message });
  }, []);

  const hideLoading = useCallback(() => {
    // Clear any pending timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Add a small delay to handle rapid show/hide cycles
    timeoutRef.current = setTimeout(() => {
      setLoadingState({ isLoading: false, message: null });
      messageRef.current = null;
      isLoadingRef.current = false;
      timeoutRef.current = null;
    }, 100);
  }, []);

  return (
    <LoadingContext.Provider
      value={{ showLoading, hideLoading, isLoading: loadingState.isLoading }}
    >
      {children}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 99999,
          display: loadingState.isLoading ? 'block' : 'none',
          pointerEvents: loadingState.isLoading ? 'auto' : 'none',
        }}
      >
        <LoadingScreen message={loadingState.message} />
      </div>
    </LoadingContext.Provider>
  );
}
